import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import { Toast } from 'primereact/toast';

import { ColumnSelector } from '../ColumnSelector';
import { FilterBuilder } from '../FilterBuilder';
import { ExportPreview } from '../ExportPreview';
import { TemplateManager } from '../TemplateManager';
import {
  TableInfo,
  ColumnInfo,
  WhereCondition,
  DynamicExportConfig,
  fetchTableColumns,
  validateDynamicExportConfig,
  exportDynamicToPdf,
  exportDynamicToExcel
} from '../../../utils/exportUtils';
import { getLocalStorageItem, setLocalStorageItem } from 'presentation/utils/localStorage';

// Função auxiliar para toLowerCase seguro
const safeToLowerCase = (value: any): string => {
  return String(value || '').toLowerCase();
};

interface DynamicExportWizardProps {
  visible: boolean;
  onHide: () => void;
  tableInfo: TableInfo;
  title?: string;
  subtitle?: string;
}

interface ExportTemplate {
  id: string;
  name: string;
  description: string;
  config: Partial<DynamicExportConfig>;
  createdAt: Date;
  updatedAt: Date;
}

export const DynamicExportWizard: React.FC<DynamicExportWizardProps> = ({
  visible,
  onHide,
  tableInfo,
  title = 'Assistente de Exportação',
  subtitle
}) => {
  const toast = useRef<Toast>(null);
  
  // Estados principais
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [availableColumns, setAvailableColumns] = useState<ColumnInfo[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<ColumnInfo[]>([]);
  const [whereConditions, setWhereConditions] = useState<WhereCondition[]>([]);
  const [exportConfig, setExportConfig] = useState<DynamicExportConfig>({
    filename: '',
    title: '',
    subtitle: '',
    tableInfo,
    selectedColumns: [],
    whereConditions: [],
    orientation: 'landscape',
    showPageNumbers: true
  });

  // Estados para templates
  const [templates, setTemplates] = useState<ExportTemplate[]>([]);
  const [currentTemplate, setCurrentTemplate] = useState<ExportTemplate | null>(null);

  // Estados para preview
  const [previewData, setPreviewData] = useState<any[]>([]);

  useEffect(() => {
    if (visible && tableInfo?.name) {
      loadTableColumns();
      loadTemplates();
      resetWizard();
    }
  }, [visible, tableInfo?.name]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setExportConfig((prev: DynamicExportConfig) => ({
      ...prev,
      tableInfo,
      selectedColumns,
      whereConditions,
      title: title || `Relatório - ${tableInfo.displayName || tableInfo.name}`,
      subtitle: subtitle || `Dados exportados da tabela ${tableInfo.displayName || tableInfo.name}`,
      filename: `relatorio_${safeToLowerCase(tableInfo.name)}`
    }));
  }, [tableInfo, selectedColumns, whereConditions, title, subtitle, availableColumns]);

  const resetWizard = () => {
    setActiveIndex(0);
    setSelectedColumns([]);
    setWhereConditions([]);
    setPreviewData([]);
    setCurrentTemplate(null);
  };

  const loadTableColumns = async () => {
    try {
      setLoading(true);
      const columns = await fetchTableColumns(tableInfo.name);
      setAvailableColumns(columns);
      
      const defaultColumns = columns.slice(0, Math.min(5, columns.length));
      setSelectedColumns(defaultColumns);
      
    } catch (error) {
      console.error(' Erro ao carregar colunas:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Erro',
        detail: 'Erro ao carregar colunas da tabela',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = () => {
    const savedTemplates = getLocalStorageItem(`export_templates_${tableInfo.name}`);
    if (savedTemplates) {
      try {
        const parsedTemplates = JSON.parse(savedTemplates);
        setTemplates(parsedTemplates);
      } catch (error) {
        console.error('Erro ao carregar templates:', error);
      }
    }
  };

  const saveTemplate = (templateData: { name: string; description: string }) => {
    const newTemplate: ExportTemplate = {
      id: Date.now().toString(),
      name: templateData.name,
      description: templateData.description,
      config: {
        selectedColumns,
        whereConditions,
        orientation: exportConfig.orientation,
        showPageNumbers: exportConfig.showPageNumbers
      },
      createdAt: new Date(),
      updatedAt: new Date()
    };

    const updatedTemplates = [...templates, newTemplate];
    setTemplates(updatedTemplates);
    setLocalStorageItem(`export_templates_${tableInfo.name}`, JSON.stringify(updatedTemplates));

    toast.current?.show({
      severity: 'success',
      summary: 'Sucesso',
      detail: 'Template salvo com sucesso',
      life: 3000
    });
  };

  const loadTemplate = (template: ExportTemplate) => {
    if (template.config.selectedColumns) {
      setSelectedColumns(template.config.selectedColumns);
    }
    if (template.config.whereConditions) {
      setWhereConditions(template.config.whereConditions);
    }
    if (template.config.orientation) {
      setExportConfig((prev: DynamicExportConfig) => ({ ...prev, orientation: template.config.orientation! }));
    }
    if (template.config.showPageNumbers !== undefined) {
      setExportConfig((prev: DynamicExportConfig) => ({ ...prev, showPageNumbers: template.config.showPageNumbers! }));
    }
    
    setCurrentTemplate(template);
    
    toast.current?.show({
      severity: 'info',
      summary: 'Template Carregado',
      detail: `Template "${template.name}" foi aplicado`,
      life: 3000
    });
  };

  const deleteTemplate = (templateId: string) => {
    const updatedTemplates = templates.filter(t => t.id !== templateId);
    setTemplates(updatedTemplates);
    setLocalStorageItem(`export_templates_${tableInfo.name}`, JSON.stringify(updatedTemplates));
    
    if (currentTemplate?.id === templateId) {
      setCurrentTemplate(null);
    }

    toast.current?.show({
      severity: 'warn',
      summary: 'Template Removido',
      detail: 'Template foi excluído com sucesso',
      life: 3000
    });
  };

  const handleNext = () => {
    if (activeIndex < 3) {
      setActiveIndex(activeIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
    }
  };

  const handleExport = async (format: 'pdf' | 'excel') => {
    const validation = validateDynamicExportConfig(exportConfig);
    
    if (!validation.valid) {
      toast.current?.show({
        severity: 'error',
        summary: 'Configuração Inválida',
        detail: validation.errors.join(', '),
        life: 5000
      });
      return;
    }

    try {
      setLoading(true);
      
      if (format === 'pdf') {
        await exportDynamicToPdf(exportConfig);
      } else {
        await exportDynamicToExcel(exportConfig);
      }

      toast.current?.show({
        severity: 'success',
        summary: 'Exportação Concluída',
        detail: `Arquivo ${format.toUpperCase()} foi gerado com sucesso`,
        life: 3000
      });

      onHide();
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Erro na Exportação',
        detail: error instanceof Error ? error.message : 'Erro desconhecido',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const renderFooter = () => (
    <div className="flex justify-content-between">
      <div>
        <Button
          label="Anterior"
          icon="pi pi-chevron-left"
          outlined
          onClick={handlePrevious}
          disabled={activeIndex === 0}
        />
      </div>
      
      <div className="flex gap-2">
        {activeIndex === 3 && (
          <>
            <Button
              label="Exportar Excel"
              icon="pi pi-file-excel"
              className="p-button-success"
              onClick={() => handleExport('excel')}
              loading={loading}
            />
            <Button
              label="Exportar PDF"
              icon="pi pi-file-pdf"
              className="p-button-danger"
              onClick={() => handleExport('pdf')}
              loading={loading}
            />
          </>
        )}
        
        {activeIndex < 3 && (
          <Button
            label="Próximo"
            icon="pi pi-chevron-right"
            iconPos="right"
            onClick={handleNext}
            disabled={activeIndex === 1 && selectedColumns.length === 0}
          />
        )}
      </div>
    </div>
  );

  return (
    <>
      <Toast ref={toast} />
      
      <Dialog
        visible={visible}
        onHide={onHide}
        header={`${title} - ${tableInfo.displayName || tableInfo.name}`}
        style={{ width: '90vw', height: '80vh' }}
        maximizable
        modal
        footer={renderFooter()}
      >
        <div className="card">
          <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
            <TabPanel header="Templates">
              <div className="flex flex-column" style={{ minHeight: '400px' }}>
                <TemplateManager
                  templates={templates}
                  currentTemplate={currentTemplate}
                  onLoadTemplate={loadTemplate}
                  onSaveTemplate={saveTemplate}
                  onDeleteTemplate={deleteTemplate}
                />
              </div>
            </TabPanel>

            <TabPanel header="Colunas">
              <div className="flex flex-column" style={{ minHeight: '400px' }}>
                <ColumnSelector
                  availableColumns={availableColumns}
                  selectedColumns={selectedColumns}
                  onSelectionChange={setSelectedColumns}
                  loading={loading}
                />
              </div>
            </TabPanel>

            <TabPanel header="Filtros">
              <div className="flex flex-column" style={{ minHeight: '400px' }}>
                {/* Debug Info */}
                <div style={{ 
                  background: '#f0f0f0', 
                  padding: '10px', 
                  margin: '10px 0', 
                  fontSize: '12px',
                  borderRadius: '4px'
                }}>
                  <strong>Debug - Colunas Disponíveis ({availableColumns.length}):</strong><br />
                  {availableColumns.map((col, i) => (
                    <span key={i}>
                      {i + 1}. {col?.name || 'undefined'} ({col?.displayName || 'sem display'}) | 
                    </span>
                  ))}
                </div>

                <FilterBuilder
                  availableColumns={availableColumns}
                  conditions={whereConditions}
                  onConditionsChange={setWhereConditions}
                />
              </div>
            </TabPanel>

            <TabPanel header="Preview & Exportar">
              <div className="flex flex-column" style={{ minHeight: '400px' }}>
                <ExportPreview
                  config={exportConfig}
                  previewData={previewData}
                  onPreviewDataChange={setPreviewData}
                  onConfigChange={setExportConfig}
                />
              </div>
            </TabPanel>
          </TabView>
        </div>
      </Dialog>
    </>
  );
};